import axiosInstance from '../api';
import {
  FetchVideoImagesReq,
  CreateVideoImageReq,
  UpdateVideoImageReq,
  DeleteVideoImageReq,
  ReorderVideoImagesReq,
  BatchDeleteVideoImagesReq,
  UpdateVideoImagesReq,
  VideoImagesRes,
  VideoImageCreateRes,
  VideoImageUpdateRes,
  VideoImageDeleteRes,
  ReorderVideoImagesRes,
  BatchDeleteVideoImagesRes,
  UpdateVideoImagesRes,
} from './videoImageApi.types';

/**
 * 動画画像一覧取得（video_id に紐づく全画像を取得）
 * @param video_id 動画ID
 * @returns 動画画像一覧
 */
export const fetchVideoImages = async (video_id: string): Promise<VideoImagesRes> => {
  try {
    const response = await axiosInstance.get<VideoImagesRes>('/video-images', {
      params: { video_id }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * 動画画像追加（multipart/form-data形式でアップロード）
 * @param video_id 動画ID
 * @param imageFile 画像ファイル
 * @param description 説明（オプション）
 * @returns API レスポンス
 */
export const createVideoImage = async (
  video_id: string,
  imageFile: File,
  description?: string
): Promise<VideoImageCreateRes> => {
  const formData = new FormData();
  formData.append('video_id', video_id);
  formData.append('description', description || '');
  formData.append('image', imageFile);

  const response = await axiosInstance.post<VideoImageCreateRes>('/video-images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * 動画画像更新（JSON形式）
 * @param id 画像ID
 * @param data 更新データ
 * @returns API レスポンス
 */
export const updateVideoImage = async (
  id: string,
  data: UpdateVideoImageReq
): Promise<VideoImageUpdateRes> => {
  const response = await axiosInstance.put<VideoImageUpdateRes>(`/video-images/${id}`, data, {
    headers: { 'Content-Type': 'application/json' }
  });
  return response.data;
};

/**
 * 動画画像削除
 * @param id 画像ID
 * @returns API レスポンス
 */
export const deleteVideoImage = async (id: string): Promise<VideoImageDeleteRes> => {
  const response = await axiosInstance.delete<VideoImageDeleteRes>(`/video-images/${id}`);
  return response.data;
};

/**
 * 画像の表示順序を更新するAPI
 * @param videoId 動画ID
 * @param imageIds 画像IDの配列（表示順に並べたもの）
 * @returns APIレスポンス
 */
export const reorderVideoImages = async (
  videoId: string,
  imageIds: string[]
): Promise<ReorderVideoImagesRes> => {
  try {
    
    const response = await axiosInstance.put<ReorderVideoImagesRes>('/video-images/reorder', {
      video_id: videoId,
      order: imageIds
    }, {
      headers: { 'Content-Type': 'application/json' }
    });
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * 動画画像の一括削除
 * @param videoId 動画ID
 * @param imageIds 削除する画像IDの配列
 * @returns API レスポンス
 */
export const batchDeleteVideoImages = async (
  videoId: string,
  imageIds: string[]
): Promise<BatchDeleteVideoImagesRes> => {
  try {
    
    const response = await axiosInstance.post<BatchDeleteVideoImagesRes>('/video-images/batch-delete', {
      video_id: videoId,
      image_ids: imageIds
    }, {
      headers: { 'Content-Type': 'application/json' }
    });
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * 動画保存時の画像一括処理
 * @param videoId 動画ID
 * @param data 一括処理データ
 * @returns API レスポンス
 */
export const updateVideoImages = async (
  videoId: string,
  data: UpdateVideoImagesReq
): Promise<UpdateVideoImagesRes> => {
  try {    
    // FormDataを作成
    const formData = new FormData();

    // 画像順序情報をJSON文字列として追加
    formData.append('image_order', JSON.stringify(data.image_order));

    // 削除する画像IDをJSON文字列として追加
    if (data.delete_image_ids && data.delete_image_ids.length > 0) {
      formData.append('delete_image_ids', JSON.stringify(data.delete_image_ids));
    }

    // 新規画像ファイルと一時IDを追加
    if (data.new_images && data.new_images.length > 0) {
      data.new_images.forEach((file, index) => {
        formData.append('new_images', file);
      });
      
      // 新規画像の一時IDをJSON文字列として追加
      if (data.new_images_temp_ids) {
        formData.append('new_images_temp_ids', JSON.stringify(data.new_images_temp_ids));
      }
      
      // 説明文も追加
      if (data.new_images_descriptions) {
        data.new_images_descriptions.forEach(desc => {
          formData.append('new_images_descriptions', desc);
        });
      }
    }
    
    const response = await axiosInstance.put<UpdateVideoImagesRes>(`/videos/${videoId}/images`, formData, {
      headers: { 
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  } catch (error) {
    throw error;
  }
}; 
